import React, { Dispatch, SetStateAction, forwardRef, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getalllocations, getalloutlettype } from "../../assign-task/api";
import { getToken } from "../../../utils/utils";
import { getRewardsFilterOptions } from "../api";
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const SELECT_ALL_LABEL = "Select All";
const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

const CustomArrow = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <span
    onMouseDown={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
      WebkitTransform: open ? "rotate(-135deg)" : "rotate(45deg)",
      WebkitTransformOrigin: "center center",
    }}
  />
);

const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 48, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 8, pointerEvents: "auto" }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try { onClick?.(); } catch { }
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{ ...(rest.inputProps || {}), style: mergedStyle, readOnly: true }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

function CustomMultiSelectWithSearch({
  label,
  options,
  value,
  onChange,
  loading,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        setSearchValue("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length === options.length && options.length > 0;
  const displayedValue =
    value.length === 0
      ? label
      : value.length <= 2
        ? value.join(", ")
        : `${value.slice(0, 2).join(", ")}…`;

  function toggleOption(option: string) {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  }

  function toggleSelectAll() {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.slice());
    }
  }

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px"
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>
          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Checkbox
                checked={allSelected}
                onChange={toggleSelectAll}
                indeterminate={value.length > 0 && !allSelected}
              />
              <Typography variant="body2" onClick={toggleSelectAll} sx={{ cursor: "pointer" }}>
                {SELECT_ALL_LABEL}
              </Typography>
            </Box>
            {filteredOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}
            {filteredOptions.map((option) => (
              <Box
                key={option}
                sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                onClick={() => toggleOption(option)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Checkbox checked={value.includes(option)} />
                <Typography variant="body2">{option}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** 🔸 helper: remove ONLY the exact "Paid" (title-case). Keeps "PAID"/"UNPAID"/etc. */
const removeExactPaid = (list: unknown[]): string[] =>
  (list as string[])
    .filter((v): v is string => typeof v === "string")
    .filter((v) => v.trim() !== "Paid");

export default function RewardsFilterPopup({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  onApplyFilters,
  onCancel,
  dateRange,
  setDateRange,
  initialFilters,
  selectedTab,
  setSelectedTab,
  selectedDate,
  setSelectedDate,
  selectedlastlogin,
  setSelectedlastlogin,
}: {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  onApplyFilters: (payload: any) => void;
  onCancel?: () => void;
  dateRange: [Date | null, Date | null];
  setDateRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
  initialFilters?: Partial<any> | null;
  selectedTab?: number;
  setSelectedTab?: Dispatch<SetStateAction<number>>;
  selectedDate?: Date | null;
  setSelectedDate?: Dispatch<SetStateAction<Date | null>>;
  selectedlastlogin?: string[];
  setSelectedlastlogin?: Dispatch<SetStateAction<string[]>>;
}) {

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevLeft = document.body.style.left;
    const prevRight = document.body.style.right;
    const prevOverflow = document.body.style.overflow;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prevPosition || "";
      document.body.style.top = prevTop || "";
      document.body.style.left = prevLeft || "";
      document.body.style.right = prevRight || "";
      document.body.style.overflow = prevOverflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletTypeOptions, setOutletTypeOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingOutletTypes, setLoadingOutletTypes] = useState(false);
  const [loadingOutletNames, setLoadingOutletNames] = useState(false);
  const [loadingPmi, setLoadingPmi] = useState(false);

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletTypes, setSelectedOutletTypes] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);
  const [memberSinceRange, setMemberSinceRange] = useState<[Date | null, Date | null]>([null, null]);

  // ⭐ NEW: Options + selections + loaders for retailer-level filters
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [asmNameOptions, setAsmNameOptions] = useState<string[]>([]);
  const [areaCodeOptions, setAreaCodeOptions] = useState<string[]>([]);
  const [tseNameOptions, setTseNameOptions] = useState<string[]>([]);
  const [territoryCodeOptions, setTerritoryCodeOptions] = useState<string[]>([]);
  const [outletSfIdOptions, setOutletSfIdOptions] = useState<string[]>([]);
  const [sfaIdOptions, setSfaIdOptions] = useState<string[]>([]);

  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedAsmNames, setSelectedAsmNames] = useState<string[]>([]);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<string[]>([]);
  const [selectedTseNames, setSelectedTseNames] = useState<string[]>([]);
  const [selectedTerritoryCodes, setSelectedTerritoryCodes] = useState<string[]>([]);
  const [selectedOutletSfIds, setSelectedOutletSfIds] = useState<string[]>([]);
  const [selectedSfaIds, setSelectedSfaIds] = useState<string[]>([]);

  const [loadingRegion, setLoadingRegion] = useState(false);
  const [loadingAsm, setLoadingAsm] = useState(false);
  const [loadingArea, setLoadingArea] = useState(false);
  const [loadingTse, setLoadingTse] = useState(false);
  const [loadingTerritory, setLoadingTerritory] = useState(false);
  const [loadingOutletSf, setLoadingOutletSf] = useState(false);
  const [loadingSfa, setLoadingSfa] = useState(false);

  // 🔹 Keep Last Login local in the popup (don’t push to header until APPLY)
  const [localDateRange, setLocalDateRange] = useState<[Date | null, Date | null]>(dateRange);

  const token = getToken();
  const filterAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);
  const skipLocationEffectRef = useRef(false);

  // NEW: suppress loop after pruning, last payload signature, debounce handle
  const suppressNextFetchRef = useRef(false);
  const lastPayloadRef = useRef<string>("");
  const debounceTimerRef = useRef<number | null>(null);

  // ⭐ include new fields in payload
  const buildDynamicPayload = () => ({
    locations: selectedLocations.length ? selectedLocations : null,
    outletTypes: selectedOutletTypes.length ? selectedOutletTypes : null,
    outletNames: selectedOutletNames.length ? selectedOutletNames : null,
    pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities : null,

    region: selectedRegions.length ? selectedRegions : null,
    asm_name: selectedAsmNames.length ? selectedAsmNames : null,
    area_code: selectedAreaCodes.length ? selectedAreaCodes : null,
    tse_name: selectedTseNames.length ? selectedTseNames : null,
    territory_code: selectedTerritoryCodes.length ? selectedTerritoryCodes : null,
    outlet_salesforce_id: selectedOutletSfIds.length ? selectedOutletSfIds : null,
    sfa_id: selectedSfaIds.length ? selectedSfaIds : null,
  });

  const fetchDynamicFilterOptions = async () => {
    const payload = buildDynamicPayload();
    const sig = JSON.stringify(payload);
    if (sig === lastPayloadRef.current) return;
    lastPayloadRef.current = sig;

    if (filterAbortRef.current) {
      try { filterAbortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    filterAbortRef.current = controller;
    try {
      setLoadingOutletNames(true);
      setLoadingPmi(true);
      setLoadingOutletTypes(true);
      setLoadingLocations(true);
      setLoadingRegion(true);
      setLoadingAsm(true);
      setLoadingArea(true);
      setLoadingTse(true);
      setLoadingTerritory(true);
      setLoadingOutletSf(true);
      setLoadingSfa(true);

      const fo = await getRewardsFilterOptions(
        payload.locations,
        token,
        controller.signal,
        {
          outletTypes: payload.outletTypes,
          outletNames: payload.outletNames,
          pmi_entity_code: payload.pmi_entity_code,

          // pass-through extra filters so backend can narrow dependent options
          region: payload.region,
          asm_name: payload.asm_name,
          area_code: payload.area_code,
          tse_name: payload.tse_name,
          territory_code: payload.territory_code,
          outlet_salesforce_id: payload.outlet_salesforce_id,
          sfa_id: payload.sfa_id,
        }
      );
      if (controller.signal.aborted) return;

      const names = Array.isArray(fo?.outletNames) ? fo.outletNames : [];
      const pmis = Array.isArray(fo?.pmiEntityCodes) ? fo.pmiEntityCodes : [];
      const dynTypes = Array.isArray((fo as any)?.availableOutletTypes) ? (fo as any).availableOutletTypes : [];
      const dynLocs = Array.isArray((fo as any)?.availableLocations) ? (fo as any).availableLocations : [];

      // ⭐ retailer-level lists returned by backend
      const dynRegions        = Array.isArray((fo as any)?.availableRegions) ? (fo as any).availableRegions : [];
      const dynAsmNames       = Array.isArray((fo as any)?.availableAsmNames) ? (fo as any).availableAsmNames : [];
      const dynAreaCodes      = Array.isArray((fo as any)?.availableAreaCodes) ? (fo as any).availableAreaCodes : [];
      const dynTseNames       = Array.isArray((fo as any)?.availableTseNames) ? (fo as any).availableTseNames : [];
      const dynTerritoryCodes = Array.isArray((fo as any)?.availableTerritoryCodes) ? (fo as any).availableTerritoryCodes : [];
      const dynOutletSfIds    = Array.isArray((fo as any)?.availableOutletSalesforceIds) ? (fo as any).availableOutletSalesforceIds : [];
      const dynSfaIds         = Array.isArray((fo as any)?.availableSfaIds) ? (fo as any).availableSfaIds : [];

      setOutletNameOptions(names.filter(Boolean));
      setPmiOptions(pmis.filter(Boolean));

      // 🔸 keep everything but remove exact "Paid"
      const dynTypesFiltered = removeExactPaid(dynTypes);
      setOutletTypeOptions(dynTypesFiltered);

      if (dynLocs.length) setLocationOptions(dynLocs.filter(Boolean));

      // ⭐ set retailer-level option lists
      setRegionOptions(dynRegions.filter(Boolean));
      setAsmNameOptions(dynAsmNames.filter(Boolean));
      setAreaCodeOptions(dynAreaCodes.filter(Boolean));
      setTseNameOptions(dynTseNames.filter(Boolean));
      setTerritoryCodeOptions(dynTerritoryCodes.filter(Boolean));
      setOutletSfIdOptions(dynOutletSfIds.filter(Boolean));
      setSfaIdOptions(dynSfaIds.filter(Boolean));

      suppressNextFetchRef.current = true;
      setSelectedOutletNames(prev => prev.filter(n => names.includes(n)));
      setSelectedPmiEntities(prev => prev.filter(p => pmis.includes(p)));
      // 🔸 prune selected: drop "Paid" and anything not in the refreshed list
      setSelectedOutletTypes(prev =>
        removeExactPaid(prev).filter((t) => dynTypesFiltered.includes(t))
      );
      if (dynLocs.length) setSelectedLocations(prev => prev.filter(l => dynLocs.includes(l)));

      // ⭐ prune new selections against refreshed lists
      setSelectedRegions(prev => prev.filter(v => dynRegions.includes(v)));
      setSelectedAsmNames(prev => prev.filter(v => dynAsmNames.includes(v)));
      setSelectedAreaCodes(prev => prev.filter(v => dynAreaCodes.includes(v)));
      setSelectedTseNames(prev => prev.filter(v => dynTseNames.includes(v)));
      setSelectedTerritoryCodes(prev => prev.filter(v => dynTerritoryCodes.includes(v)));
      setSelectedOutletSfIds(prev => prev.filter(v => dynOutletSfIds.includes(v)));
      setSelectedSfaIds(prev => prev.filter(v => dynSfaIds.includes(v)));
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("Error fetching dynamic filterOptions:", err);
      }
    } finally {
      if (filterAbortRef.current === controller) filterAbortRef.current = null;
      setLoadingOutletNames(false);
      setLoadingPmi(false);
      setLoadingOutletTypes(false);
      setLoadingLocations(false);
      setLoadingRegion(false);
      setLoadingAsm(false);
      setLoadingArea(false);
      setLoadingTse(false);
      setLoadingTerritory(false);
      setLoadingOutletSf(false);
      setLoadingSfa(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    // reset signature so a `{null}` payload triggers a call
    lastPayloadRef.current = "";

    let cancelled = false;
    const fetchLists = async () => {
      try {
        setLoadingLocations(true);
        setLoadingOutletTypes(true);
        const [locRes, otRes] = await Promise.allSettled([getalllocations(), getalloutlettype()]);
        if (cancelled) return;

        if (locRes.status === "fulfilled") {
          const locVal = locRes.value;
          const locs = Array.isArray(locVal?.data?.data) ? locVal.data.data : Array.isArray(locVal?.data) ? locVal.data : [];
          setLocationOptions(locs as string[]);
        } else {
          console.error("getalllocations failed:", (locRes as any).reason);
          setLocationOptions([]);
        }

        if (otRes.status === "fulfilled") {
          const otVal = otRes.value;
          const ots = Array.isArray(otVal?.data?.data) ? otVal.data.data : Array.isArray(otVal?.data) ? otVal.data : [];
          // 🔸 initial list: remove only "Paid"
          const otsFiltered = removeExactPaid(ots as string[]);
          setOutletTypeOptions(otsFiltered);
          // also clean any pre-selected "Paid"
          setSelectedOutletTypes((prev) => removeExactPaid(prev));
        } else {
          console.error("getalloutlettype failed:", (otRes as any).reason);
          setOutletTypeOptions([]);
        }

        // always kick dynamic options once on open (even when nothing is selected)
        setLoadingOutletNames(true);
        setLoadingPmi(true);
        await fetchDynamicFilterOptions();

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Error loading initial lists:", err);
        }
        setLocationOptions([]);
        setOutletTypeOptions([]);
      } finally {
        if (!cancelled) {
          setLoadingLocations(false);
          setLoadingOutletTypes(false);
          setLoadingOutletNames(false);
          setLoadingPmi(false);
        }
      }
    };

    fetchLists();
    return () => {
      cancelled = true;
      if (filterAbortRef.current) {
        try { filterAbortRef.current.abort(); } catch {}
        filterAbortRef.current = null;
      }
      onFetchedCalledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);


  useEffect(() => {
    if (!isOpen) return;

    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }

    if (skipLocationEffectRef.current) {
      skipLocationEffectRef.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      fetchDynamicFilterOptions();
      debounceTimerRef.current = null;
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocations, selectedOutletTypes, selectedOutletNames, selectedPmiEntities, isOpen,
    // ⭐ NEW deps
    selectedRegions, selectedAsmNames, selectedAreaCodes, selectedTseNames, selectedTerritoryCodes, selectedOutletSfIds, selectedSfaIds
  ]);

  useEffect(() => {
    return () => {
      if (filterAbortRef.current) {
        try { filterAbortRef.current.abort(); } catch (e) { }
        filterAbortRef.current = null;
      }
    };
  }, []);

  // 🔹 Rehydrate local state from initialFilters only; DO NOT push to parent here
  useEffect(() => {
    if (!initialFilters) return;
    setSelectedLocations(initialFilters.locations ?? []);
    // 🔸 sanitize incoming selections to drop only "Paid"
    setSelectedOutletTypes(removeExactPaid(initialFilters.outletTypes ?? []));
    setSelectedOutletNames(initialFilters.outletNames ?? []);
    setSelectedPmiEntities(initialFilters.pmi_entity_code ?? []);

    // ⭐ NEW: rehydrate retailer-level selections
    setSelectedRegions(initialFilters.region ?? []);
    setSelectedAsmNames(initialFilters.asm_name ?? []);
    setSelectedAreaCodes(initialFilters.area_code ?? []);
    setSelectedTseNames(initialFilters.tse_name ?? []);
    setSelectedTerritoryCodes(initialFilters.territory_code ?? []);
    setSelectedOutletSfIds(initialFilters.outlet_salesforce_id ?? []);
    setSelectedSfaIds(initialFilters.sfa_id ?? []);

    const lastStart = initialFilters.lastloginStart ? new Date(initialFilters.lastloginStart) : null;
    const lastEnd = initialFilters.lastloginEnd ? new Date(initialFilters.lastloginEnd) : null;
    setLocalDateRange([lastStart, lastEnd]);

    const memStartStr = (initialFilters.memberSinceStart ?? (initialFilters as any).member_since_start) ?? null;
    const memEndStr = (initialFilters.memberSinceEnd ?? (initialFilters as any).member_since_end) ?? null;
    setMemberSinceRange([
      memStartStr ? new Date(memStartStr) : null,
      memEndStr ? new Date(memEndStr) : null,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  const formatDateForPayload = (d: Date | null): string | null => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

  const clearAll = () => {
    skipLocationEffectRef.current = true;
    setSelectedLocations([]);
    setSelectedOutletTypes([]);
    setSelectedOutletNames([]);
    setSelectedPmiEntities([]);
    setLocalDateRange([null, null]);
    setMemberSinceRange([null, null]);

    // ⭐ NEW: clear retailer-level selections
    setSelectedRegions([]);
    setSelectedAsmNames([]);
    setSelectedAreaCodes([]);
    setSelectedTseNames([]);
    setSelectedTerritoryCodes([]);
    setSelectedOutletSfIds([]);
    setSelectedSfaIds([]);

    // Clear the header range too (global reset)
    setDateRange([null, null]);

    if (typeof setSelectedTab === "function") {
      try { setSelectedTab(0); } catch { }
    }
    if (typeof setSelectedDate === "function") {
      try { setSelectedDate(null); } catch { }
    }
    if (typeof setSelectedlastlogin === "function") {
      try { setSelectedlastlogin([]); } catch { }
    }

    lastPayloadRef.current = "";

    const payload: any = {
      locations: null,
      outletTypes: null,
      outletNames: null,
      pmi_entity_code: null,

      // ⭐ NEW
      region: null,
      asm_name: null,
      area_code: null,
      tse_name: null,
      territory_code: null,
      outlet_salesforce_id: null,
      sfa_id: null,

      lastloginStart: null,
      lastloginEnd: null,
      memberSinceStart: null,
      memberSinceEnd: null,
      member_since_start: null,
      member_since_end: null,
      backendFilters: {
        city: null,
        outlet_category: null,
        fromdate: null,
        todate: null,
        member_since_start: null,
        member_since_end: null,
      },
    };
    try {
      onApplyFilters(payload);
    } catch (e) {
      console.error("onApplyFilters threw during clearAll:", e);
    }
    setTimeout(() => {
      skipLocationEffectRef.current = false;
      try {
        onCancel?.();
      } catch (e) {
        console.error("onCancel threw during clearAll:", e);
      }
      fetchDynamicFilterOptions();
    }, 0);
  };

  const handleApply = () => {
    const [lastStart, lastEnd] = localDateRange;
    const [memberStart, memberEnd] = memberSinceRange;
    const mStart = formatDateForPayload(memberStart);
    const mEnd = formatDateForPayload(memberEnd);
    const payload: any = {
      locations: selectedLocations.length > 0 ? selectedLocations.slice() : null,
      outletTypes: selectedOutletTypes.length > 0 ? selectedOutletTypes.slice() : null,
      outletNames: selectedOutletNames.length > 0 ? selectedOutletNames.slice() : null,
      pmi_entity_code: selectedPmiEntities.length > 0 ? selectedPmiEntities.slice() : null,

      // ⭐ NEW
      region: selectedRegions.length ? selectedRegions.slice() : null,
      asm_name: selectedAsmNames.length ? selectedAsmNames.slice() : null,
      area_code: selectedAreaCodes.length ? selectedAreaCodes.slice() : null,
      tse_name: selectedTseNames.length ? selectedTseNames.slice() : null,
      territory_code: selectedTerritoryCodes.length ? selectedTerritoryCodes.slice() : null,
      outlet_salesforce_id: selectedOutletSfIds.length ? selectedOutletSfIds.slice() : null,
      sfa_id: selectedSfaIds.length ? selectedSfaIds.slice() : null,

      lastloginStart: formatDateForPayload(lastStart),
      lastloginEnd: formatDateForPayload(lastEnd),
      memberSinceStart: mStart,
      memberSinceEnd: mEnd,
      member_since_start: mStart,
      member_since_end: mEnd,
      backendFilters: {
        city: selectedLocations.length > 0 ? selectedLocations.slice() : null,
        outlet_category: selectedOutletTypes.length > 0 ? selectedOutletTypes.slice() : null,
        fromdate: formatDateForPayload(localDateRange[0]),
        todate: formatDateForPayload(localDateRange[1]),
        member_since_start: mStart,
        member_since_end: mEnd,
      },
    };

    // 🔹 NOW push the chosen range to the header (parent) on APPLY
    setDateRange([lastStart ?? null, lastEnd ?? null]);

    onApplyFilters(payload);
  };

  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };

  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderHeaderWithIcons = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    decreaseYear: () => void;
    increaseYear: () => void;
  }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
        <img src={doubleArrowLeft} alt="Previous year" />
      </button>
      <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
        <img src={arrowLeft} alt="Previous month" />
      </button>
      <span style={{ margin: "0 10px", fontWeight: 600 }}>
        {date.toLocaleString("default", { year: "numeric", month: "long" })}
      </span>
      <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
        <img src={arrowRight} alt="Next month" />
      </button>
      <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
        <img src={doubleArrowRight} alt="Next year" />
      </button>
    </div>
  );

  return (
    <Box sx={{ width: 420, maxHeight: "80vh", background: "#fff", borderRadius: 2, p: 0, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filter</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <CustomMultiSelectWithSearch
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            loading={loadingLocations}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Types</Typography>
          <CustomMultiSelectWithSearch
            label="Select Outlet Types"
            options={outletTypeOptions}
            value={selectedOutletTypes}
            onChange={setSelectedOutletTypes}
            loading={loadingOutletTypes}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <CustomMultiSelectWithSearch
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={setSelectedOutletNames}
            loading={loadingOutletNames}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity</Typography>
          <CustomMultiSelectWithSearch
            label="Select PMI Entity"
            options={pmiOptions}
            value={selectedPmiEntities}
            onChange={setSelectedPmiEntities}
            loading={loadingPmi}
          />
        </Box>

        {/* ⭐ NEW retailer-level filters */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Region</Typography>
          <CustomMultiSelectWithSearch
            label="Select Region"
            options={regionOptions}
            value={selectedRegions}
            onChange={setSelectedRegions}
            loading={loadingRegion}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select ASM Name</Typography>
          <CustomMultiSelectWithSearch
            label="Select ASM Name"
            options={asmNameOptions}
            value={selectedAsmNames}
            onChange={setSelectedAsmNames}
            loading={loadingAsm}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Area Code</Typography>
          <CustomMultiSelectWithSearch
            label="Select Area Code"
            options={areaCodeOptions}
            value={selectedAreaCodes}
            onChange={setSelectedAreaCodes}
            loading={loadingArea}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select TSE Name</Typography>
          <CustomMultiSelectWithSearch
            label="Select TSE Name"
            options={tseNameOptions}
            value={selectedTseNames}
            onChange={setSelectedTseNames}
            loading={loadingTse}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Territory Code</Typography>
          <CustomMultiSelectWithSearch
            label="Select Territory Code"
            options={territoryCodeOptions}
            value={selectedTerritoryCodes}
            onChange={setSelectedTerritoryCodes}
            loading={loadingTerritory}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Salesforce ID</Typography>
          <CustomMultiSelectWithSearch
            label="Select Outlet Salesforce ID"
            options={outletSfIdOptions}
            value={selectedOutletSfIds}
            onChange={setSelectedOutletSfIds}
            loading={loadingOutletSf}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select SFA ID</Typography>
          <CustomMultiSelectWithSearch
            label="Select SFA ID"
            options={sfaIdOptions}
            value={selectedSfaIds}
            onChange={setSelectedSfaIds}
            loading={loadingSfa}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Last Login</Typography>
          <Box sx={{ width: "100%" }}>
            <GlobalStyles
              styles={{
                ".react-datepicker-wrapper": { width: "100% !important" },
                '.MuiAutocomplete-option, .MuiAutocomplete-option[aria-selected="true"], .MuiAutocomplete-option.Mui-focused, .MuiAutocomplete-option:hover, li[role="option"], li[role="option"][aria-selected="true"], li[role="option"].Mui-focusVisible':
                  { background: "transparent !important" },
                ".MuiTouchRipple-root": { display: "none !important" },
              }}
            />
            <DatePicker
              selectsRange
              startDate={localDateRange[0] ?? undefined}
              endDate={localDateRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => {
                // 🔹 Keep it local; do not update parent here
                setLocalDateRange(range);
              }}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(localDateRange)}
                  key={`last-${localDateRange[0]?.getTime() ?? 0}-${localDateRange[1]?.getTime() ?? 0}`}
                  sx={{ width: "100%" }}
                  InputProps={{
                    endAdornment:
                      (localDateRange[0] || localDateRange[1]) ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            // 🔹 Clear local only; parent updates on APPLY or CLEAR ALL
                            setLocalDateRange([null, null]);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Member Since</Typography>
          <Box sx={{ width: "100%" }}>
            <DatePicker
              selectsRange
              startDate={memberSinceRange[0] ?? undefined}
              endDate={memberSinceRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => setMemberSinceRange(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(memberSinceRange)}
                  key={`mem-${memberSinceRange[0]?.getTime() ?? 0}-${memberSinceRange[1]?.getTime() ?? 0}`}
                  sx={{ width: "100%" }}
                  InputProps={{
                    endAdornment:
                      (memberSinceRange[0] || memberSinceRange[1]) ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setMemberSinceRange([null, null]);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: 2.5, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
        <Button onClick={clearAll} sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}>
          CLEAR ALL
        </Button>
        <Button onClick={handleApply} sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}>
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
